/**
 * @fileoverview SkillSystem - スキル管理システム
 */

import { ROWS, COLS, GameStatus } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { getSkillDefinition } from '../../gameplay/skills/definitions.js';
import { executeSkillEffect } from '../../gameplay/skills/executor.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';
import { canInteract } from '../../shared/utils/game-state-utils.js';
import { SKILL_CONSTANTS, DOM_CONSTANTS } from '../constants.js';
import { updateAllButtons } from '../controllers/button-controller.js';
import {
  addPointerListeners,
  resolveBoardCellFromPointerEvent,
} from '../handlers/pointer-input.js';

import { guardAsyncButton } from './button-guard.js';

const isDrawerOpen = () =>
  document.documentElement.classList.contains(DOM_CONSTANTS.DRAWER_OPEN_CLASS);

export class SkillSystem {
  constructor() {
    this.skillButton = null;
    this.eventHandlers = { cleanup: null };
    this.isInitialized = false;
  }

  initialize() {
    if (this.isInitialized) return;

    this.skillButton = document.getElementById('skill-button');
    this.setupSkillButton();
    this.isInitialized = true;
  }

  async handleSkillButtonClick(_event) {
    const ui = gameState.getState(STATE_PATHS.UI);

    // ターゲティング中なら解除
    if (ui.isSkillTargeting) {
      this.exitTargeting();
      return;
    }

    if (!this.canActivateSkill()) return;

    // 選択中スキルを取得
    const selectedSkill = gameState.getState(STATE_PATHS.SETTINGS_SELECTED_SKILL);
    const skillDef = getSkillDefinition(selectedSkill);

    if (!skillDef) {
      MinimalErrorTracker.warn('SKILL', `Unknown skill: ${selectedSkill}`);
      return;
    }

    // ターゲティング要否で分岐
    if (skillDef.requiresTarget) {
      // パターンA: ターゲティングモード開始
      this.startTargeting();
    } else {
      // パターンB: 即座に実行
      await this.executeSkillImmediate(skillDef);
    }
  }

  async handleCellClick(row, col, _source) {
    if (!this.canExecuteSkill(row, col)) return;

    try {
      const ui = gameState.getState(STATE_PATHS.UI);
      const before = ui.remainingSkillUses; // ロールバック用

      // 楽観的減算
      gameState.setState(STATE_PATHS.UI_REMAINING_SKILL_USES, before - 1);

      this.exitTargeting();

      // スキル実行シーケンス全体の開始時に処理状態を設定
      // (SwapやStartと同じパターン)
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Processing);

      const selectedSkill = gameState.getState(STATE_PATHS.SETTINGS_SELECTED_SKILL);
      const result = await this.executeSkill(selectedSkill, { row, col });

      // 失敗時はロールバック
      if (!result || !result.ok) {
        gameState.setState(STATE_PATHS.UI_REMAINING_SKILL_USES, before);
        MinimalErrorTracker.warn('SKILL', `Skill execution failed, rolled back uses`);
      }
    } finally {
      // スキル実行シーケンス全体完了後にReady状態にリセット
      // (SwapやStartと同じパターン)
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready);
    }
  }

  async executeSkill(skillType, target) {
    try {
      // executor経由で実行（エフェクト表示含む）
      const result = await executeSkillEffect(skillType, target);

      if (!result.ok) {
        MinimalErrorTracker.warn('SKILL', `Skill execution failed: ${result.reason}`);
      }

      return result;
    } catch (error) {
      MinimalErrorTracker.error('SKILL', 'Skill execution error', error);
      return { ok: false, reason: 'execution_error', error };
    }
  }

  /**
   * ターゲティング不要スキルの即座実行
   * @param {Object} skillDef - スキル定義
   * @returns {Promise<void>}
   */
  async executeSkillImmediate(skillDef) {
    try {
      const ui = gameState.getState(STATE_PATHS.UI);
      const before = ui.remainingSkillUses; // ロールバック用

      // 楽観的減算
      gameState.setState(STATE_PATHS.UI_REMAINING_SKILL_USES, before - 1);

      // 処理状態に遷移
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Processing);

      // スキル実行（target = null）
      const result = await this.executeSkill(skillDef.skillType, null);

      // 失敗時はロールバック
      if (!result || !result.ok) {
        gameState.setState(STATE_PATHS.UI_REMAINING_SKILL_USES, before);
        MinimalErrorTracker.warn('SKILL', `Skill execution failed, rolled back uses`);
      }
    } finally {
      // Ready状態に復帰（必ずfinallyで実行）
      gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready);
    }
  }

  /**
   * スキル発動可能性チェック
   */
  canActivateSkill() {
    const ui = gameState.getState(STATE_PATHS.UI);
    return !ui.isSkillTargeting && canInteract(gameState) && ui.remainingSkillUses > 0;
  }

  /**
   * スキル実行可能性チェック
   */
  canExecuteSkill(row, col) {
    const ui = gameState.getState(STATE_PATHS.UI);
    return ui.isSkillTargeting && ui.remainingSkillUses > 0 && this.isValidTarget(row, col);
  }

  /**
   * ターゲット座標検証
   */
  isValidTarget(row, col) {
    return row >= 0 && col >= 0 && row < ROWS && col < COLS;
  }

  /**
   * ターゲティングモード開始
   */
  async startTargeting() {
    // 状態更新
    gameState.setState(STATE_PATHS.UI_IS_SKILL_TARGETING, true, 'startTargeting');

    // UI更新
    await this.updateSkillButtonUI(true);

    // イベントハンドラー設定
    this.setupEventHandlers();

    // ボタン状態を明示的に更新
    updateAllButtons(gameState);
  }

  /**
   * ターゲティングモード終了
   */
  async exitTargeting() {
    // 状態更新
    gameState.setState(STATE_PATHS.UI_IS_SKILL_TARGETING, false, 'exitTargeting');

    // UI更新
    await this.updateSkillButtonUI(false);

    // イベントハンドラークリーンアップ
    this.cleanupEventHandlers();

    // ボタン状態を明示的に更新
    updateAllButtons(gameState);
  }

  /**
   * スキルボタンUI更新
   */
  updateSkillButtonUI(isActive) {
    if (!this.skillButton) return;

    // 状態切り替え
    document.documentElement.classList.toggle('is-skill-targeting', isActive);
    this.skillButton.classList.toggle('is-armed', isActive);
    this.skillButton.setAttribute('aria-pressed', isActive.toString());

    if (isActive) {
      // パルスアニメーション
      this.skillButton.style.transform =
        'translate(calc(-50% + var(--skill-translateX)), -50%) scale(0.96)';
      setTimeout(() => {
        this.skillButton.style.transform =
          'translate(calc(-50% + var(--skill-translateX)), -50%) scale(1)';
      }, SKILL_CONSTANTS.BUTTON_ANIMATION_DURATION);
    }
  }

  /**
   * スキルボタンイベント設定
   */
  setupSkillButton() {
    if (!this.skillButton) return;

    this.skillButton.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.handleSkillButtonClick(ev);
    });
  }

  /**
   * セルクリックイベント設定
   */
  setupEventHandlers() {
    if (this.eventHandlers.cleanup) {
      return;
    }

    const boardElement = document.getElementById('board');
    if (!boardElement) {
      MinimalErrorTracker.warn('SKILL', 'Board element not found for pointer setup');
      return;
    }

    let removePointerListeners = null;
    const protectedHandler = guardAsyncButton(async (row, col, source) => {
      await this.handleCellClick(row, col, source);
    });

    removePointerListeners = addPointerListeners(
      boardElement,
      {
        onDown: (event) => {
          event.stopPropagation();
          // スキルターゲティング中は常に受理
          return true;
        },
        onUp: (event) => {
          event.stopPropagation();
          const cellInfo = resolveBoardCellFromPointerEvent(event, boardElement);
          if (!cellInfo) {
            return;
          }

          const { row, col } = cellInfo;
          if (typeof col === 'number' && typeof row === 'number' && col >= 0 && row >= 0) {
            protectedHandler(row, col, 'pointerup');
          }
        },
      },
      {
        capture: true,
        preventDefault: true,
        shouldProcess: () => !isDrawerOpen(),
        getDedupeKey: (event) =>
          `${Math.round(event.clientX)}:${Math.round(event.clientY)}:${event.pointerType}`,
      },
    );

    this.eventHandlers.cleanup = () => {
      removePointerListeners?.();
      removePointerListeners = null;
      this.eventHandlers.cleanup = null;
    };
  }

  /**
   * イベントハンドラークリーンアップ
   */
  cleanupEventHandlers() {
    if (typeof this.eventHandlers.cleanup === 'function') {
      this.eventHandlers.cleanup();
    }
  }
}

// シングルトンインスタンス
export const skillSystem = new SkillSystem();
